import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  ArrowLeft, Minus, Plus, Trash2, ShoppingCart, Truck, Store, Wallet,
  Smartphone, ShieldCheck, Loader2, MapPin,
} from "lucide-react";
import {
  loadCart, updateCartQty, removeFromCart, clearCart, cartTotals, formatXAF, grocery,
} from "../../lib/grocery";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../../lib/api";
import PaymentSuccessOverlay from "../../components/PaymentSuccessOverlay";

export default function CartCheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cart, setCartItems] = useState(loadCart());
  const [zones, setZones] = useState([]);
  const [expressSurcharge, setExpressSurcharge] = useState(500);

  // form
  const [fulfillment, setFulfillment] = useState("delivery"); // delivery | pickup
  const [zoneSlug, setZoneSlug] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [msisdn, setMsisdn] = useState("");
  const [express, setExpress] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pickupShops, setPickupShops] = useState([]); // shop metadata for pickup (multi-shop)

  // sandbox confirm
  const [stkPayment, setStkPayment] = useState(null);
  const [otp, setOtp] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [overlay, setOverlay] = useState(null);

  useEffect(() => {
    grocery.zones().then((d) => {
      setZones(d.zones || []);
      setExpressSurcharge(d.express_surcharge_xaf ?? 500);
    });
  }, []);

  const totals = useMemo(() => cartTotals(cart), [cart]);
  const isPickup = fulfillment === "pickup";
  const zone = zones.find((z) => z.slug === zoneSlug);
  const deliveryFee = isPickup ? 0 : (zone ? zone.delivery_fee : 0) + (express ? expressSurcharge : 0);
  const total = totals.subtotal_xaf + deliveryFee;

  const changeQty = (pid, delta) => {
    const item = cart.find((c) => c.product_id === pid);
    if (!item) return;
    const next = updateCartQty(pid, item.quantity + delta);
    setCartItems(next);
  };

  const handleRemove = (pid) => {
    const next = removeFromCart(pid);
    setCartItems(next);
  };


  // Fetch shop addresses when switching to pickup mode
  useEffect(() => {
    if (!isPickup || cart.length === 0) {
      setPickupShops([]);
      return;
    }
    const items = cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity }));
    grocery
      .estimate({
        items,
        fulfillment_type: "pickup",
        customer_phone: "00000000",
        payment_method: "cash",
      })
      .then((r) => setPickupShops(r.pickup_addresses || []))
      .catch(() => setPickupShops([]));
  }, [isPickup, cart]);

  const validate = () => {
    if (!user || user === false) {
      toast.error("Connectez-vous pour passer commande");
      navigate("/login?next=/courses/panier");
      return false;
    }
    if (cart.length === 0) {
      toast.error("Votre panier est vide");
      return false;
    }
    if (!isPickup) {
      if (!zoneSlug) {
        toast.error("Choisissez votre zone de livraison");
        return false;
      }
      if (address.trim().length < 3) {
        toast.error("Indiquez une adresse de livraison valide");
        return false;
      }
    }
    if (phone.trim().length < 4) {
      toast.error("Indiquez un numéro de téléphone");
      return false;
    }
    if (paymentMethod !== "cash" && msisdn.trim().length < 4) {
      toast.error("Indiquez votre numéro Mobile Money");
      return false;
    }
    return true;
  };

  const handleCheckout = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const items = cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity }));
      const resp = await grocery.checkout({
        items,
        fulfillment_type: fulfillment,
        delivery_zone: isPickup ? null : zoneSlug,
        delivery_address: isPickup ? "" : address.trim(),
        customer_phone: phone.trim(),
        payment_method: paymentMethod,
        msisdn: paymentMethod === "cash" ? null : msisdn.trim(),
        express: isPickup ? false : express,
        notes,
      });
      if (resp.status === "order_created") {
        toast.success(isPickup ? "Commande créée — à régler au retrait" : "Commande créée — paiement à la livraison");
        clearCart();
        navigate(`/courses/commande/${resp.order_id}`);
      } else if (resp.status === "stk_push_sent") {
        toast.success("Demande de paiement envoyée à votre téléphone");
        setStkPayment(resp);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Échec de la commande");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmSandbox = async () => {
    if (!stkPayment) return;
    if (otp.length !== 4) {
      toast.error("Code OTP à 4 chiffres requis");
      return;
    }
    setConfirming(true);
    try {
      const r = await grocery.confirmSandbox(stkPayment.payment_id, otp);
      setOverlay({
        provider: stkPayment.provider,
        amountLabel: formatXAF(stkPayment.amount_xaf),
        reference: stkPayment.reference,
        orderId: r.order_id,
      });
      clearCart();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "OTP incorrect");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-brand-paper pb-24" data-testid="grocery-checkout-page">
      <section className="bg-brand-ink text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-5">
          <Link to="/courses" className="text-white/70 text-sm flex items-center gap-1 mb-2 hover:text-brand-gold" data-testid="grocery-back-to-shop">
            <ArrowLeft className="w-4 h-4" /> Continuer les courses
          </Link>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold inline-flex items-center gap-2">
            <ShoppingCart className="w-6 h-6" /> Mon panier
          </h1>
          <p className="text-sm text-white/70 mt-1">{totals.count} article{totals.count > 1 ? "s" : ""}</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Items */}
        <div className="lg:col-span-2 space-y-3">
          {cart.length === 0 ? (
            <Card className="p-8 rounded-2xl border-brand-line text-center">
              <ShoppingCart className="w-12 h-12 text-brand-line mx-auto mb-3" />
              <p className="font-heading font-semibold text-brand-ink mb-1">Panier vide</p>
              <p className="text-sm text-brand-inkSoft mb-4">Ajoutez des produits depuis nos boutiques.</p>
              <Link to="/courses">
                <Button className="bg-brand-gold hover:bg-brand-goldDark text-brand-black" data-testid="grocery-empty-go-shop">
                  Découvrir les produits
                </Button>
              </Link>
            </Card>
          ) : (
            cart.map((c) => (
              <Card
                key={c.product_id}
                className="p-3 sm:p-4 rounded-2xl border-brand-line flex items-center gap-3"
                data-testid={`grocery-cart-item-${c.product_id}`}
              >
                <div className="w-14 h-14 rounded-xl bg-brand-sandlight flex items-center justify-center shrink-0">
                  <span className="text-2xl">🛒</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-brand-ink text-sm leading-tight">{c.name}</div>
                  <div className="text-[11px] text-brand-inkSoft uppercase tracking-wider">par {c.unit}</div>
                  <div className="text-brand-goldDark font-bold mt-1">{formatXAF(c.price_xaf * c.quantity)}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => changeQty(c.product_id, -1)}
                    className="w-8 h-8 p-0 rounded-lg"
                    data-testid={`grocery-qty-minus-${c.product_id}`}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-6 text-center font-bold" data-testid={`grocery-qty-${c.product_id}`}>{c.quantity}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => changeQty(c.product_id, 1)}
                    className="w-8 h-8 p-0 rounded-lg"
                    data-testid={`grocery-qty-plus-${c.product_id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemove(c.product_id)}
                    className="w-8 h-8 p-0 rounded-lg text-brand-terracotta hover:text-brand-terracotta hover:bg-red-50"
                    data-testid={`grocery-remove-${c.product_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))
          )}

          {/* Delivery / Pickup form */}
          {cart.length > 0 && (
            <Card className="p-5 rounded-2xl border-brand-line space-y-4" data-testid="grocery-delivery-form">
              <h3 className="font-heading font-semibold text-brand-ink inline-flex items-center gap-2">
                {isPickup ? <Store className="w-5 h-5 text-brand-gold" /> : <Truck className="w-5 h-5 text-brand-gold" />}
                {isPickup ? "Retrait en magasin" : "Livraison à domicile"}
              </h3>

              {/* Fulfillment toggle */}
              <div className="grid grid-cols-2 gap-2 p-1 rounded-2xl bg-brand-sandlight" data-testid="grocery-fulfillment-toggle">
                <button
                  type="button"
                  onClick={() => setFulfillment("delivery")}
                  data-testid="grocery-fulfillment-delivery"
                  className={`py-3 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all ${
                    !isPickup ? "bg-brand-ink text-brand-gold shadow-md" : "text-brand-inkSoft"
                  }`}
                >
                  <Truck className="w-4 h-4" />
                  Livraison
                </button>
                <button
                  type="button"
                  onClick={() => setFulfillment("pickup")}
                  data-testid="grocery-fulfillment-pickup"
                  className={`py-3 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all ${
                    isPickup ? "bg-brand-ink text-brand-gold shadow-md" : "text-brand-inkSoft"
                  }`}
                >
                  <Store className="w-4 h-4" />
                  Retrait en magasin
                </button>
              </div>

              {!isPickup && (
                <>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-brand-inkSoft mb-2 block">Zone de livraison *</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {zones.map((z) => (
                        <button
                          key={z.slug}
                          type="button"
                          onClick={() => setZoneSlug(z.slug)}
                          data-testid={`grocery-zone-${z.slug}`}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            zoneSlug === z.slug
                              ? "bg-brand-ink text-brand-gold border-brand-ink"
                              : "bg-white border-brand-line hover:border-brand-gold text-brand-ink"
                          }`}
                        >
                          <div className="font-semibold text-sm">{z.name}</div>
                          <div className={`text-xs ${zoneSlug === z.slug ? "text-brand-gold/80" : "text-brand-inkSoft"}`}>
                            {formatXAF(z.delivery_fee)}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address" className="text-xs uppercase tracking-wider text-brand-inkSoft">Adresse précise *</Label>
                    <Input
                      id="address"
                      data-testid="grocery-address-input"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Ex : Quartier Chagoua, rue 3, en face de la mosquée"
                      className="h-12 mt-1"
                    />
                  </div>

                  <div className="flex items-center justify-between bg-brand-sandlight rounded-xl p-3">
                    <div>
                      <div className="font-semibold text-sm text-brand-ink inline-flex items-center gap-2">
                        ⚡ Livraison Express
                      </div>
                      <div className="text-xs text-brand-inkSoft">+{formatXAF(expressSurcharge)} · livré en moins de 60 min</div>
                    </div>
                    <Switch
                      checked={express}
                      onCheckedChange={setExpress}
                      data-testid="grocery-express-switch"
                    />
                  </div>
                </>
              )}

              {isPickup && (
                <div className="space-y-2" data-testid="grocery-pickup-addresses">
                  <Label className="text-xs uppercase tracking-wider text-brand-inkSoft block">
                    Point{pickupShops.length > 1 ? "s" : ""} de retrait ({pickupShops.length})
                  </Label>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800 inline-flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Frais de livraison <strong>0 FCFA</strong>. Présentez-vous avec votre numéro de commande.</span>
                  </div>
                  {pickupShops.length === 0 ? (
                    <p className="text-sm text-brand-inkSoft">Calcul des points de retrait…</p>
                  ) : (
                    <div className="space-y-2">
                      {pickupShops.map((s, i) => (
                        <Card key={s.shop_id} className="p-3 rounded-xl border-brand-line bg-white" data-testid={`grocery-pickup-shop-${i}`}>
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-brand-sandlight text-brand-gold flex items-center justify-center shrink-0">
                              <Store className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm text-brand-ink">{s.name}</div>
                              <div className="text-xs text-brand-inkSoft inline-flex items-center gap-1 mt-0.5">
                                <MapPin className="w-3 h-3" />{s.address || "Adresse à confirmer"}
                              </div>
                              {s.phone && (
                                <div className="text-xs text-brand-inkSoft mt-0.5">{s.phone}</div>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-brand-inkSoft">
                  {isPickup ? "Votre téléphone *" : "Téléphone du livreur *"}
                </Label>
                <Input
                  id="phone"
                  data-testid="grocery-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+235 6X XX XX XX"
                  inputMode="tel"
                  className="h-12 mt-1"
                />
              </div>

              <div>
                <Label htmlFor="notes" className="text-xs uppercase tracking-wider text-brand-inkSoft">Notes (optionnel)</Label>
                <Textarea
                  id="notes"
                  data-testid="grocery-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={isPickup ? "Heure prévue de retrait, particularités…" : "Étage, code interphone, particularités…"}
                  rows={2}
                  className="mt-1"
                />
              </div>

              {/* Payment */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-brand-inkSoft mb-2 block">Mode de paiement *</Label>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <PayOption value="cash" current={paymentMethod} title="Espèces" subtitle="À la livraison" icon={<Wallet className="w-5 h-5" />} />
                  <PayOption value="airtel" current={paymentMethod} title="Airtel Money" subtitle="STK Push" icon={<Smartphone className="w-5 h-5" />} accent="#E60000" />
                  <PayOption value="moov" current={paymentMethod} title="Moov Money" subtitle="STK Push" icon={<Smartphone className="w-5 h-5" />} accent="#003DA5" />
                </RadioGroup>
                {paymentMethod !== "cash" && (
                  <div className="mt-3">
                    <Label htmlFor="msisdn" className="text-xs uppercase tracking-wider text-brand-inkSoft">Numéro Mobile Money *</Label>
                    <Input
                      id="msisdn"
                      data-testid="grocery-msisdn-input"
                      value={msisdn}
                      onChange={(e) => setMsisdn(e.target.value)}
                      placeholder="+235 6X XX XX XX"
                      inputMode="tel"
                      className="h-12 mt-1"
                    />
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Summary */}
        {cart.length > 0 && (
          <aside className="lg:col-span-1">
            <Card className="p-5 rounded-2xl border-brand-line lg:sticky lg:top-24 space-y-3" data-testid="grocery-summary">
              <h3 className="font-heading font-semibold text-brand-ink">Récapitulatif</h3>
              <div className="flex justify-between text-sm">
                <span className="text-brand-inkSoft">Sous-total</span>
                <span className="font-semibold text-brand-ink" data-testid="grocery-summary-subtotal">{formatXAF(totals.subtotal_xaf)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-brand-inkSoft">
                  {isPickup ? "Frais de retrait" : `Livraison${express ? " (Express)" : ""}`}
                </span>
                <span className="font-semibold text-brand-ink" data-testid="grocery-summary-fee">
                  {isPickup ? formatXAF(0) : (zone ? formatXAF(deliveryFee) : "—")}
                </span>
              </div>
              <div className="border-t border-brand-line my-2" />
              <div className="flex justify-between items-baseline">
                <span className="text-brand-ink font-semibold">Total</span>
                <span className="font-heading text-2xl font-bold text-brand-goldDark" data-testid="grocery-summary-total">
                  {formatXAF(total)}
                </span>
              </div>
              <Button
                onClick={handleCheckout}
                disabled={submitting}
                className="w-full h-12 rounded-xl bg-brand-gold hover:bg-brand-goldDark text-brand-black font-semibold text-base shadow-lg mt-2"
                data-testid="grocery-checkout-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ShieldCheck className="w-5 h-5 mr-2" />}
                {paymentMethod === "cash" ? "Confirmer la commande" : "Payer maintenant"}
              </Button>
              <div className="text-[11px] text-brand-inkSoft text-center">
                Paiement sécurisé · 100% N'Djamena
              </div>
            </Card>
          </aside>
        )}
      </section>

      {/* OTP sandbox modal */}
      {stkPayment && !overlay && (
        <div
          className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center bg-black/65 backdrop-blur-sm p-4"
          data-testid="grocery-otp-modal"
        >
          <Card className="w-full max-w-md rounded-3xl p-6 bg-white">
            <div className="text-center mb-4">
              <div
                className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center text-white"
                style={{ backgroundColor: stkPayment.provider === "moov" ? "#003DA5" : "#E60000" }}
              >
                <Smartphone className="w-7 h-7" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-brand-ink">
                Confirmer le paiement
              </h3>
              <p className="text-sm text-brand-inkSoft mt-1">
                Saisissez le code OTP envoyé au <strong>{stkPayment.msisdn}</strong>
              </p>
              <div className="font-heading text-3xl font-bold text-brand-goldDark mt-3">
                {formatXAF(stkPayment.amount_xaf)}
              </div>
            </div>

            {stkPayment.sandbox_otp_hint && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 mb-3" data-testid="grocery-otp-hint">
                <strong>Mode sandbox</strong> · Code OTP : <span className="font-mono font-bold text-base">{stkPayment.sandbox_otp_hint}</span>
              </div>
            )}

            <Input
              data-testid="grocery-otp-input"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="• • • •"
              className="h-14 text-center text-2xl tracking-[1em] font-bold"
              inputMode="numeric"
              maxLength={4}
            />

            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => { setStkPayment(null); setOtp(""); }}
                className="flex-1 h-11"
                data-testid="grocery-otp-cancel"
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirmSandbox}
                disabled={confirming || otp.length !== 4}
                className="flex-1 h-11 bg-brand-gold hover:bg-brand-goldDark text-brand-black font-semibold"
                data-testid="grocery-otp-confirm"
              >
                {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {overlay && (
        <PaymentSuccessOverlay
          provider={overlay.provider}
          amountLabel={overlay.amountLabel}
          reference={overlay.reference}
          onDone={() => {
            navigate(`/courses/commande/${overlay.orderId}`);
          }}
        />
      )}
    </div>
  );
}

function PayOption({ value, current, title, subtitle, icon, accent }) {
  const active = current === value;
  return (
    <label
      data-testid={`grocery-pay-${value}`}
      className={`relative flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
        active ? "border-brand-gold bg-brand-sandlight" : "border-brand-line bg-white hover:border-brand-gold/60"
      }`}
    >
      <RadioGroupItem value={value} id={`pay-${value}`} className="shrink-0" />
      <div className="flex-1">
        <div
          className="font-semibold text-sm text-brand-ink inline-flex items-center gap-1.5"
          style={accent ? { color: active ? accent : "#0F0F0F" } : undefined}
        >
          {icon}
          {title}
        </div>
        <div className="text-[11px] text-brand-inkSoft">{subtitle}</div>
      </div>
    </label>
  );
}
